"use client";

import { useCallback, useId, useRef, useState } from "react";
import Image from "next/image";
import type { BeforeAfterCase } from "@/lib/before-after";

type BeforeAfterPairProps = {
  case: BeforeAfterCase;
  priority?: boolean;
};

/**
 * Interactive draggable-divider before/after pair.
 *
 * Pointer events drive a single offset (`position`, 0..100) which clips
 * the after-frame layer with `clip-path: inset(...)`. Both pointer and
 * keyboard interactions are supported so the slider is operable on
 * mobile, on desktop, and via assistive tech.
 *
 * Treatment label, interval, age band, and notes render below the pair.
 * Consented cases also render a low-key "Consented for use" badge.
 */
export function BeforeAfterPair({
  case: data,
  priority = false,
}: BeforeAfterPairProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const labelId = useId();

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    const raw = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(0, Math.min(100, raw));
    setPosition(clamped);
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromClientX(event.clientX);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setPosition((p) => Math.max(0, p - 5));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      setPosition((p) => Math.min(100, p + 5));
    } else if (event.key === "Home") {
      event.preventDefault();
      setPosition(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setPosition(100);
    }
  };

  const isPlaceholder = data.status === "placeholder";

  return (
    <figure className="flex flex-col">
      <div
        ref={containerRef}
        role="slider"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        aria-labelledby={labelId}
        aria-valuetext={`${Math.round(position)}% before, ${Math.round(100 - position)}% after`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        className="silk-img-rounded relative aspect-[4/5] w-full overflow-hidden bg-cream select-none touch-none cursor-ew-resize focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
      >
        {/* Before frame, full bleed */}
        <Image
          src={data.beforeImage}
          alt={`Before: ${data.treatment.replace(/-/g, " ")}, ${data.interval}`}
          fill
          preload={priority}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover"
          draggable={false}
        />

        {/* After frame, clipped to the right of the divider */}
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        >
          <Image
            src={data.afterImage}
            alt={`After: ${data.treatment.replace(/-/g, " ")}, ${data.interval}`}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover"
            draggable={false}
          />
        </div>

        {/* Corner labels */}
        <span className="pointer-events-none absolute top-3 left-3 z-10 bg-charcoal/70 text-white text-[10px] font-medium tracking-[0.22em] uppercase px-2 py-1">
          Before
        </span>
        <span className="pointer-events-none absolute top-3 right-3 z-10 bg-charcoal/70 text-white text-[10px] font-medium tracking-[0.22em] uppercase px-2 py-1">
          After
        </span>

        {/* Divider line and handle */}
        <div
          className="pointer-events-none absolute inset-y-0 z-20 flex items-center justify-center"
          style={{ left: `calc(${position}% - 0.5px)`, width: "1px" }}
        >
          <span
            className="absolute inset-y-0 w-px"
            style={{ backgroundColor: "var(--color-champagne)" }}
          />
          <span
            className="relative block h-9 w-9 rounded-full border bg-offwhite shadow-[0_1px_4px_rgba(26,26,26,0.18)]"
            style={{ borderColor: "var(--color-champagne)" }}
            aria-hidden="true"
          >
            <span
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase"
              style={{ color: "var(--color-champagne-dark)" }}
            >
              ↔
            </span>
          </span>
        </div>

        {/* Consent / placeholder badge */}
        {!isPlaceholder ? (
          <span className="pointer-events-none absolute bottom-3 right-3 z-10 bg-offwhite/85 text-charcoal/65 text-[9px] font-medium tracking-[0.22em] uppercase px-2 py-1">
            Consented for use
          </span>
        ) : (
          <span className="pointer-events-none absolute bottom-3 right-3 z-10 bg-offwhite/85 text-charcoal/55 text-[9px] font-medium tracking-[0.22em] uppercase px-2 py-1">
            Placeholder
          </span>
        )}
      </div>

      <figcaption className="mt-4 flex flex-col gap-1.5">
        <p
          id={labelId}
          className="silk-eyebrow"
          style={{ color: "var(--color-champagne-dark)" }}
        >
          <span className="silk-eyebrow-num">↳</span>
          <span
            className="silk-eyebrow-rule"
            style={{ backgroundColor: "var(--color-champagne)" }}
          />
          <span>{data.treatment.replace(/-/g, " ")}</span>
        </p>
        <p className="text-[13px] text-charcoal/70 tracking-[0.02em]">
          <span>{data.interval}</span>
          {data.ageBand ? (
            <>
              <span className="mx-2 text-charcoal/30">/</span>
              <span>Age band: {data.ageBand}</span>
            </>
          ) : null}
        </p>
        {data.notes ? (
          <p className="mt-1 text-[14px] leading-[1.6] text-charcoal/75">
            {data.notes}
          </p>
        ) : null}
      </figcaption>
    </figure>
  );
}
